import * as THREE from 'three'
import { useEffect, useRef, useState } from 'react'
import { Canvas } from '@react-three/fiber'
import { Center, AccumulativeShadows, RandomizedLight, OrbitControls, Environment, useGLTF, useAnimations } from '@react-three/drei'
import { FlakesTexture } from 'three-stdlib'
import { Volume2, VolumeX, Play, Loader } from 'lucide-react'

export default function App() {
  const audioRef = useRef(null)
  const [isMuted, setIsMuted] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [needsUserInteraction, setNeedsUserInteraction] = useState(true)
  const [isModelLoaded, setIsModelLoaded] = useState(false)
  const [loadingProgress, setLoadingProgress] = useState(0)

  // Don't initialize audio until model is loaded
  useEffect(() => {
    if (!isModelLoaded || !audioRef.current) return

    // Create audio element for background music
    audioRef.current.volume = 0.3 // Set moderate volume
    audioRef.current.loop = true

    // Listen for when audio can start playing
    const handleCanPlay = () => {
      // Try to autoplay (browsers may block this)
      const playPromise = audioRef.current.play()
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true)
            setNeedsUserInteraction(false)
          })
          .catch((error) => {
            console.log('Autoplay prevented:', error)
            setNeedsUserInteraction(true)
          })
      }
    }

    audioRef.current.addEventListener('canplaythrough', handleCanPlay)

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('canplaythrough', handleCanPlay)
      }
    }
  }, [isModelLoaded])

  const startMusic = () => {
    if (audioRef.current && isModelLoaded) {
      audioRef.current
        .play()
        .then(() => {
          setIsPlaying(true)
          setNeedsUserInteraction(false)
          setIsMuted(false)
        })
        .catch((error) => {
          console.log('Play failed:', error)
        })
    }
  }

  const toggleMute = () => {
    if (audioRef.current && isModelLoaded) {
      if (isMuted) {
        audioRef.current.volume = 0.3
        if (!isPlaying) {
          audioRef.current.play()
          setIsPlaying(true)
        }
      } else {
        audioRef.current.volume = 0
      }
      setIsMuted(!isMuted)
    }
  }

  // Handle any user interaction to start music (only if model is loaded)
  const handleUserInteraction = () => {
    if (needsUserInteraction && !isPlaying && isModelLoaded) {
      startMusic()
    }
  }

  return (
    <div
      style={{ width: '100vw', height: '100vh', position: 'relative' }}
      onClick={handleUserInteraction}
      onTouchStart={handleUserInteraction}
      onKeyDown={handleUserInteraction}>
      {/* Audio element - only load after model is ready */}
      {isModelLoaded && (
        <audio ref={audioRef} preload="auto" style={{ display: 'none' }}>
          <source src="music.mp3" type="audio/mp3" />
          Your browser does not support the audio element.
        </audio>
      )}

      {/* Loading Screen */}
      {!isModelLoaded && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            color: 'white'
          }}>
          <div
            style={{
              marginBottom: '30px',
              animation: 'spin 2s linear infinite'
            }}>
            <Loader size={64} />
          </div>
          <h2
            style={{
              margin: '0 0 20px 0',
              fontSize: '24px',
              fontWeight: '300',
              letterSpacing: '2px'
            }}>
            Loading Experience
          </h2>
          <div
            style={{
              width: '200px',
              height: '4px',
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '2px',
              overflow: 'hidden',
              marginBottom: '20px'
            }}>
            <div
              style={{
                width: `${loadingProgress}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #4facfe 0%, #00f2fe 100%)',
                borderRadius: '2px',
                transition: 'width 0.3s ease'
              }}
            />
          </div>
          <p
            style={{
              margin: 0,
              fontSize: '14px',
              opacity: 0.8
            }}>
            {Math.round(loadingProgress)}% Complete
          </p>
        </div>
      )}

      {/* Play Button Overlay (only shown when model is loaded and user interaction is needed) */}
      {isModelLoaded && needsUserInteraction && !isPlaying && (
        <div
          onClick={startMusic}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 2000,
            background: 'rgba(0, 0, 0, 0.8)',
            border: '2px solid white',
            borderRadius: '50%',
            width: '80px',
            height: '80px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            transition: 'all 0.3s ease',
            animation: 'pulse 2s infinite'
          }}>
          <Play size={32} fill="white" />
        </div>
      )}

      {/* Mute/Unmute Button (only shown when model is loaded) */}
      {isModelLoaded && (
        <button
          onClick={toggleMute}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            zIndex: 1000,
            background: 'rgba(0, 0, 0, 0.7)',
            border: 'none',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => (e.target.style.background = 'rgba(0, 0, 0, 0.9)')}
          onMouseLeave={(e) => (e.target.style.background = 'rgba(0, 0, 0, 0.7)')}>
          {isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
        </button>
      )}

      {/* Instructions for first-time users (only shown when model is loaded) */}
      {isModelLoaded && needsUserInteraction && !isPlaying && (
        <div
          style={{
            fontWeight: 'bold',
            position: 'absolute',
            bottom: '30px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            background: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: '10px 20px',
            borderRadius: '20px',
            textAlign: 'center',
            fontSize: '14px'
          }}>
          Click anywhere to start the music experience
        </div>
      )}

      {/* 3D Canvas - only render when model is loaded */}
      {isModelLoaded && (
        <Canvas shadows camera={{ position: [4, 2.5, 8], fov: 45 }}>
          <group position={[0, -0.5, 0]}>
            <Center top>
              <CustomModel position={[0, 1.1, 0]} scale={2} onLoad={() => setIsModelLoaded(true)} onProgress={(progress) => setLoadingProgress(progress)} />
            </Center>
            <Center top position={[2.5, 0, 1]}>
              <mesh castShadow rotation={[0, Math.PI / 4, 0]}>
                <boxGeometry args={[0.5, 0.5, 0.5]} />
                <meshStandardMaterial color="indianred" />
              </mesh>
            </Center>
          </group>
          <OrbitControls minPolarAngle={Math.PI / 4} maxPolarAngle={Math.PI / 2.1} minDistance={3} maxDistance={20} />
          <Environment preset="sunset" />
        </Canvas>
      )}

      {/* Hidden Canvas for loading model */}
      {!isModelLoaded && (
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
          <Canvas>
            <CustomModel onLoad={() => setIsModelLoaded(true)} onProgress={(progress) => setLoadingProgress(progress)} />
          </Canvas>
        </div>
      )}

      <style jsx>{`
        @keyframes pulse {
          0% {
            transform: translate(-50%, -50%) scale(1);
          }
          50% {
            transform: translate(-50%, -50%) scale(1.1);
          }
          100% {
            transform: translate(-50%, -50%) scale(1);
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  )
}

function CustomModel({ onLoad, ...props }) {
  const { scene, animations } = useGLTF('/3.compressed.glb')
  const { actions } = useAnimations(animations, scene)

  // Enable shadow casting for all meshes in the model
  useEffect(() => {
    scene.traverse((node) => {
      if (node.isMesh) {
        node.castShadow = true
      }
    })

    // Call onLoad when model is ready
    if (onLoad) {
      onLoad()
    }
  }, [scene, onLoad])

  // Play all animations when the component mounts
  useEffect(() => {
    if (actions) {
      Object.values(actions).forEach((action) => {
        if (action) {
          action.reset()
          action.setLoop(THREE.LoopRepeat, Infinity)
          action.play()
        }
      })
    }
  }, [actions])

  return <primitive object={scene} {...props} />
}
